import type { StockItemResult } from '../_actions/searchLibraryImages';
import { VideoGenerationFormState } from './VideoGenerationFormState';

export type VoiceoverGenerationSubmitState = {
    status: AiToolkitSubmitStatus;
};

export type VideoGenerationPollResponse = {
    status: 'SUCCEEDED' | 'FAILED' | 'CANCELLED';
    videoUrl?: string | null;
};

export type VideoGenerationSubmitState = {
    pendingTaskId: string | null;
    videoUrl: string;
    aiContentId: string;
    lastAiContentId: string;
    status: AiToolkitSubmitStatus;
    restoredFromHistory: boolean;
};

export type ImageGenerationSubmitState = {
    status: AiToolkitSubmitStatus;
};

export type ImageGenerationSubmitAction =
    | { type: 'GENERATION_IN_PROGRESS' }
    | { type: 'GENERATION_SUCCESS' }
    | { type: 'GENERATION_ERROR' }
    | { type: 'RESET' };

export type VoiceoverGenerationSubmitAction =
    | { type: 'GENERATION_IN_PROGRESS' }
    | { type: 'GENERATION_SUCCESS' }
    | { type: 'GENERATION_ERROR' }
    | { type: 'RESET' };

export type VideoGenerationSubmitAction =
    | { type: 'GENERATION_IN_PROGRESS'; pendingTaskId?: string; aiContentId?: string; lastAiContentId?: string }
    | { type: 'GENERATION_SUCCESS'; videoUrl: string; aiContentId: string; lastAiContentId: string }
    | { type: 'GENERATION_ERROR' }
    | { type: 'RESET' };

export type AiToolkitActiveStatus = 'inProgress' | 'success' | 'error';
export type AiToolkitSubmitStatus = 'idle' | AiToolkitActiveStatus;

export type ConfirmationModalState =
    | { type: 'regenerate'; category: AiToolkitCategory }
    | { type: 'insufficientCredits'; category: AiToolkitCategory }
    | { type: 'entInsufficientCredits'; category: AiToolkitCategory }
    | { type: 'entFeatureAccess'; category: AiToolkitCategory }
    | { type: 'creditConfirmation'; category: AiToolkitCategory }
    | null;

export type ConfirmationModalAction =
    | { type: 'OPEN_MODAL'; modal: NonNullable<ConfirmationModalState> }
    | { type: 'CLOSE_MODAL' };

export const AI_TOOLKIT_CATEGORIES = {
    VIDEO_GENERATION: 'video-generation',
    // VIDEO_EDIT: 'video-edit',
    IMAGE_GENERATION: 'image-generation',
    VOICEOVER: 'voiceover',
} as const;

export type AiToolkitCategory = (typeof AI_TOOLKIT_CATEGORIES)[keyof typeof AI_TOOLKIT_CATEGORIES];

export type ActiveTask = {
    category: AiToolkitCategory;
    status: AiToolkitActiveStatus;
} | null;

export type AiToolkitFormStates = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]?: VideoGenerationFormState;
    [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]?: null;
    [AI_TOOLKIT_CATEGORIES.VOICEOVER]?: null;
};

export type AiToolkitSubmitStates = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: VideoGenerationSubmitState;
    [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]: ImageGenerationSubmitState;
    [AI_TOOLKIT_CATEGORIES.VOICEOVER]: VoiceoverGenerationSubmitState;
};

export const AI_USER_SEGMENTS = {
    UNSUBSCRIBED: 'unsubscribed',
    UPGRADABLE: 'upgradable',
    SUBSCRIBED: 'subscribed',
} as const;

export type AiUserSegment = (typeof AI_USER_SEGMENTS)[keyof typeof AI_USER_SEGMENTS];

export type AiToolkitContextValue = {
    credits: number;
    isEnterprise: boolean;
    subscriptionId: number | null;
    categorySegments: Record<AiToolkitCategory, AiUserSegment>;
    activeTask: ActiveTask;
    refreshCredits: () => Promise<number>;
    formStates: AiToolkitFormStates;
    submitStates: AiToolkitSubmitStates;
    updateFormState: <K extends keyof AiToolkitFormStates>(category: K, state: AiToolkitFormStates[K]) => void;
    dispatchVideoGenerationSubmit: (action: VideoGenerationSubmitAction) => void;
    dispatchImageGenerationSubmit: (action: ImageGenerationSubmitAction) => void;
    dispatchVoiceoverGenerationSubmit: (action: VoiceoverGenerationSubmitAction) => void;
    confirmationModal: ConfirmationModalState;
    dispatchConfirmationModal: (action: ConfirmationModalAction) => void;
    imageSearch: ImageSearchReturn;
};

export const SUPPORTED_IMAGE_SEARCH_MEDIA_TYPES = ['Photos', 'Illustrations', 'Snapshots'] as const;
export type SupportedImageSearchMediaType = (typeof SUPPORTED_IMAGE_SEARCH_MEDIA_TYPES)[number];

export const SUPPORTED_IMAGE_SEARCH_FILE_TYPES = ['JPG', 'PNG'] as const;
export type SupportedImageSearchFileType = (typeof SUPPORTED_IMAGE_SEARCH_FILE_TYPES)[number];

export type ImageSearchReturn = {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    mediaType: SupportedImageSearchMediaType;
    setMediaType: (type: SupportedImageSearchMediaType) => void;
    results: StockItemResult[];
    isLoading: boolean;
    error: string | null;
    totalCount: number;
    hasMore: boolean;
    loadMore: () => Promise<void>;
    search: () => Promise<void>;
    reset: () => void;
    currentPage: number;
    totalPages: number;
    goToPage: (page: number) => Promise<void>;
    showSearch: boolean;
    setShowSearch: (show: boolean) => void;
};
